/**
 * 組織レポート v3（社長向け・PC）。
 *
 * 正典: apps/web/docs/新方針20260821/仕様_GB組織レポートの生成ロジック.html
 *
 * 【描画方式】前バージョン(b7)と同じHTML生成方式。teamReportGeneratorV3 が
 * テンプレートCSSに乗るHTMLを組み立て、iframe に描いて表示する。
 * PDFも既存の generateTeamReportPdf（1440px固定iframe）をそのまま使う。
 *
 * 【モバイル版との関係】算出・文言は同じモジュールを共有し（useOrgReportV3Data）、
 * 見せ方だけが違う。モバイルは /embed/report/org（縦スクロール）。
 */
import { useMemo, useState } from 'react';
import { Box, Button, Typography, Alert, CircularProgress } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useOrgReportV3Data, formatReportDate } from '../hooks/useOrgReportV3Data';
import { generateOrgReportV3Html } from '../../aggregation/utils/teamReportGeneratorV3';
import { generateTeamReportPdf } from '../../aggregation/utils/pdfGenerator';

export const OrgReportV3Page: React.FC = () => {
  const { data, loading, error } = useOrgReportV3Data();
  const [saving, setSaving] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const dateStr = useMemo(() => formatReportDate(new Date()), []);
  const html = useMemo(() => (data ? generateOrgReportV3Html(data, dateStr) : ''), [data, dateStr]);
  /** 前バージョンと同じ経路（1440px固定iframe）でA4横PDFにする。 */
  const handleSavePdf = async () => {
    if (!html) return;
    try {
      setSaving(true);
      setPdfError(null);
      await generateTeamReportPdf(html, `${dateStr} 組織レポート.pdf`);
    } catch (e) {
      setPdfError(e instanceof Error ? e.message : 'PDFの作成に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" py={8} gap={2}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          組織レポートを作成しています…
        </Typography>
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Box p={3}>
        <Alert severity="info">{error ?? 'レポートを表示できませんでした。'}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">組織レポート</Typography>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
          onClick={() => void handleSavePdf()}
          disabled={saving}
        >
          {saving ? 'PDFを作成中…' : 'PDFとして保存する'}
        </Button>
      </Box>

      {pdfError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPdfError(null)}>
          {pdfError}
        </Alert>
      )}

      {/*
        レポートは iframe に「実ドキュメント」として描く。
        アプリ側のCSSがテンプレートCSSに干渉しないようにするため（前バージョンと同じ考え方）。
      */}
      <Box
        component="iframe"
        title="組織レポート"
        srcDoc={html}
        sx={{
          width: '100%',
          height: '80vh',
          border: '1px solid #E0E0E0',
          borderRadius: 1,
          background: '#fff',
        }}
      />
    </Box>
  );
};

export default OrgReportV3Page;
